import { useCallback, useEffect, useRef, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { loadCollectionsAsync } from '../../data-layer/collections/actions';
import {
    selectCollections,
    selectCollectionsError,
    selectCollectionsLoading,
    selectCreatedCollections,
    selectSavedCollections,
} from '../../data-layer/collections/selectors';
import { CollectionModel } from '../../data-layer/collections/types';
import { useDispatch } from '../../data-layer/useDispatch';
import { useSelector } from '../../data-layer/useSelector';

export interface UseCollectionsControllerPayload {
    collections: CollectionModel[];
    createdCollections: CollectionModel[];
    savedCollections: CollectionModel[];
    isLoading: boolean;
    isLoaded: boolean;
    isError: boolean;
    loadCollections: VoidFunction;
}

export interface UseCollectionsControllerParams {
    businessId?: string;
    namespace?: string;
    requestCondition?: boolean;
    skipSavedCollections?: boolean;
}

export const useCollectionsController = (
    params?: UseCollectionsControllerParams,
): UseCollectionsControllerPayload => {
    const dispatch = useDispatch();
    const namespace = params?.namespace || params?.businessId || 'default';
    const [isLoaded, setIsLoaded] = useState(false);
    const prevLoadingRef = useRef<boolean>();

    const collections = useSelector((state) => selectCollections(state, namespace));
    const createdCollections = useSelector((state) => selectCreatedCollections(state, namespace));
    const savedCollections = useSelector((state) => selectSavedCollections(state, namespace));
    const isLoading = useSelector((state) => selectCollectionsLoading(state, namespace));
    const isError = useSelector((state) => selectCollectionsError(state, namespace));

    const loadCollections = useCallback(() => {
        if (!isLoading) {
            dispatch(
                loadCollectionsAsync(params?.businessId, {
                    skipSavedCollections: params?.skipSavedCollections,
                }),
            );
        }
    }, [dispatch, isLoading, params?.businessId, params?.skipSavedCollections]);

    useEffect(() => {
        if (prevLoadingRef.current === true && !isLoading) {
            setIsLoaded(true);
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    useEffectOnce(() => {
        if (typeof params?.requestCondition === 'boolean') {
            return;
        }

        const needsLoad = params?.skipSavedCollections
            ? !createdCollections.length
            : !createdCollections.length || !savedCollections.length;

        if (needsLoad) {
            loadCollections();
        }
    });

    useEffect(() => {
        if (params?.requestCondition === true) {
            loadCollections();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.requestCondition]);

    return {
        collections,
        createdCollections,
        savedCollections,
        isLoading,
        isLoaded,
        isError,
        loadCollections,
    };
};
